import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import { GridColDef } from '@mui/x-data-grid-pro';

import {
  DataGrid,
  Page,
  StyledButton,
  StyledIconButton
} from '../../components/interface';

import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import { PainRow } from '../../models/PainRow';

import { fetch_all, fetch_one } from '../../lib/server_helper';
import { loginAtom } from '../../lib/auth';

import { PAIN_ACCOUNTS } from './accounts';

const { DateTime } = require("luxon");

const formatEur = (v: number | string) => parseFloat(String(v || 0)).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

type PainRowsProps = {};

export const PainRows: FunctionComponent<PainRowsProps> = () => {
  const navigate = useNavigate();
  const params = useParams();

  const [login] = useAtom(loginAtom);

  const accountType = params.account_type || '';
  const account = PAIN_ACCOUNTS.find((a) => a.name.toLowerCase() === accountType);

  const [rows, setRows] = useState<PainRow[]>([]);
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>('updated_at DESC');
  const [removeLoader, setRemoveLoader] = useState<boolean>(false);
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);

  useEffect(() => {
    onSearch();
  }, [page, order]);

  const onSearch = () => {
    setLoader(true);
    fetch_all<PainRow>('/nl/v3/objects/fetch_all', {
      object: 'pain_row',
      fields: ['id', 'created_at', 'updated_at', 'account_type', 'amount', 'name', 'iban', 'note', 'user_id'],
      per_page: 20,
      page: page,
      order: order,
      filter: {
        advanced: {
          account_type: accountType,
          filter_done: 0
        }
      }
    }, (obs, complete_data) => {
      setRows(obs);

      let count = 0;
      if (typeof(complete_data?.data) === 'object' && 'count' in complete_data?.data) count = complete_data?.data?.count;
      setRowsCount(count);

      setLoader(false);
    }, (z) => {
      setServerErrorSnackOpen(true);
    }, login);
  };

  const onRemove = (id: number) => {
    setRemoveLoader(true);
    fetch_one('/nl/v3/objects/remove', {
      object: 'pain_row',
      ob: {
        id: id
      }
    }, (ob, complete_data) => {
      setRemoveLoader(false);
      onSearch();
    }, (z) => {
      setRemoveLoader(false);
      setServerErrorSnackOpen(true);
    }, login);
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 250 },
    { field: 'amount', headerName: 'Amount (EUR)', width: 150, align: 'right', valueFormatter: (params) => formatEur(params?.value || 0) },
    { field: 'iban', headerName: 'IBAN', width: 220 },
    { field: 'note', headerName: 'Note', width: 350, sortable: false },
    { field: 'updated_at', headerName: 'Updated', width: 200, valueFormatter: (params) => DateTime.fromISO(params?.value).toFormat('D TT') },
    { field: 'id', headerName: 'ID', width: 100, sortable: false }
  ];

  return (
    <Page title={`PAIN rows${!!account ? ` - ${account.name}` : ''}`} actions={<>
      <StyledButton
        label="Add row"
        id='add_row'
        contained
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => {
          navigate('/create_pain/new', {state: {account_type: accountType}});
        }}
        sx={{marginRight: 3}}
      />
    </>}
    startEndCrumb={<StyledButton
      label="Back to overview"
      id='back'
      text
      startIcon={<KeyboardBackspaceIcon />}
      path={`/create_pain`}
      sx={{marginBottom: 1}}
    />}>
      <DataGrid
        action_count={2}
        actions={(params) => {
          return (<>
            <StyledIconButton color="primary" title="Edit row" onClick={() => {
              navigate(`/create_pain/edit/${params.row.id}`);
            }}>
              <EditIcon />
            </StyledIconButton>
            {!removeLoader && <StyledIconButton destroyBtn color="primary" title="Remove row" onClick={(e) => {
              onRemove(params.row.id);
            }}>
              <DeleteForeverIcon />
            </StyledIconButton>}
          </>);
        }}
        objects={rows}
        columns={columns}
        objectCount={rowsCount}
        loader={loader}
        onSortModelChange={(a, b) => {
          let a_0_field = a[0].field;
          setOrder([a_0_field, a[0].sort].join(' '));
        }}
        page={page}
        setPage={(p) => {setPage(p);}}
      />

      {!!serverErrorSnackOpen && <AlertSomethingWentWrong open={serverErrorSnackOpen} setOpen={(b) => {setServerErrorSnackOpen(b);}} />}
    </Page>
  );
}
